import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ContactSearchComponent from 'components/app/support-desk/tickets-preview/ContactSearchComponent';
import MessageTemplateAutocomplete from 'components/common/MessageTemplateAutocomplete';
import MessageTemplateSelector from 'components/common/MessageTemplateSelector';
import Editor from 'components/editor/Editor';
import Loading from 'components/loading/Loading';
import useApi from 'helpers/api';
import { processHtmlForEmail } from 'helpers/utils';
import useNotification from 'hooks/useNotification';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { Button, Card, Dropdown, Form } from 'react-bootstrap';

import AgentsService from 'services/Agents.service';

const ReplyNoteComponent = ({
  show,
  handleClose,
  ticketId,
  customerId,
  contact,
  refreshData,
  refreshNotes,
  refreshLogs,
  ticketStatus
}) => {
  if (!show) return null; // Não renderiza se show=false

  const { showSuccessNotification, showErrorNotification } = useNotification();
  const api = useApi();
  const form = useRef(null);
  const editorRef = useRef(null);
  const [validated, setValidated] = useState(false);
  const [body, setBody] = useState({
    note: '',
    typeView: 'PUBLIC',
    cc: []
  });
  const [loading, setLoading] = useState(false);

  const [cc, setCc] = useState('');

  useEffect(() => {
    const fetchSignature = async () => {
      try {
        const response = await AgentsService.getSignature();
        if (response.signature) {
          const signature = `<br><br>${response.signature}`;
          setBody(prev => ({ ...prev, note: signature }));
        }
      } catch (error) {
        console.error('Error fetching signature:', error);
      }
    };

    if (show) {
      clearFields();
      setValidated(false);
      fetchSignature();
    }
  }, [show]);

  useEffect(() => {
    const emailsArray = cc
      .split(';')
      .map(email => email.trim())
      .filter(email => email);

    setBody({ ...body, cc: emailsArray });
  }, [cc]);

  const onSubmit = async e => {
    setLoading(true);
    if (
      form.current.checkValidity() === false ||
      body.note.trim() === '' ||
      body.note === '<p><br></p>'
    ) {
      e.preventDefault();
      e.stopPropagation();
      setValidated(true);
      setLoading(false);
      return;
    }

    const processedNote = processHtmlForEmail(body.note);
    const processedBody = { ...body, note: processedNote };

    let response = await api.ticketNotes.reply(ticketId, processedBody);
    handleResponse(response);
    clearFields();
    handleClose();
    setLoading(false);
  };

  const replyAndSetAsPending = async () => {
    setLoading(true);
    if (
      form.current.checkValidity() === false ||
      body.note.trim() === '' ||
      body.note === '<p><br></p>'
    ) {
      setValidated(true);
      setLoading(false);
      return;
    }

    let response = await api.ticketNotes.replyAndSetAsPending(ticketId, body);
    handleResponse(response);
    if (refreshData) refreshData();
    clearFields();
    handleClose();
    setLoading(false);
  };

  const replyAndSetAsCompleted = async () => {
    setLoading(true);
    if (
      form.current.checkValidity() === false ||
      body.note.trim() === '' ||
      body.note === '<p><br></p>'
    ) {
      setValidated(true);
      setLoading(false);
      return;
    }

    let response = await api.ticketNotes.replyAndSetAsCompleted(ticketId, body);
    handleResponse(response);
    if (refreshData) refreshData();
    clearFields();
    handleClose();
    setLoading(false);
  };

  const handleSetBody = (fieldName, fieldValue) => {
    setBody(prevBody => ({ ...prevBody, [fieldName]: fieldValue }));
  };

  const clearFields = () => {
    setBody({
      note: '',
      typeView: 'PUBLIC',
      cc: []
    });
    setCc('');
  };

  function handleResponse(response) {
    if (response.id) {
      showSuccessNotification('Nota adicionada com sucesso');
      if (refreshLogs) refreshLogs();
      if (refreshNotes) refreshNotes();
      // Se não houver refreshNotes disponível, tenta usar a função getNotes de TicketInfo
      // que é passada via ref na implementação atual
    } else {
      showErrorNotification(response.userMessage);
    }
  }

  const currentContact = contact;
  return (
    <>
      {loading && <Loading />}

      <Card>
        <Card.Body className="pb-0">
          <Form ref={form} validated={validated} noValidate>
            {/* Para */}
            <div className="mb-2">
              <Form.Label className="mb-1 fs-10">
                Para <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={currentContact && currentContact.email}
                disabled
                required
              />
              <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                Campo obrigatório.
              </Form.Control.Feedback>
            </div>

            {/* Com Cópia (CC) */}
            <ContactSearchComponent customerId={customerId} onChange={setCc} />

            {/* Descrição */}
            <div className="mb-2">
              <Form.Label className="mb-1 fs-10 d-flex align-items-center justify-content-between">
                <span>
                  Descrição <span className="text-danger">*</span>
                </span>
                <MessageTemplateSelector
                  type="EMAIL"
                  ticketId={ticketId}
                  editorRef={editorRef}
                  onTemplateApplied={content => {
                    if (!editorRef.current) {
                      handleSetBody('note', content);
                    }
                  }}
                />
              </Form.Label>
              <div className="position-relative">
                <Editor
                  ref={editorRef}
                  value={value => handleSetBody('note', value)}
                  height="300px"
                  data={body.note}
                  required
                />
                <MessageTemplateAutocomplete
                  editorRef={editorRef}
                  type="EMAIL"
                  ticketId={ticketId}
                  onTemplateApplied={content => {
                    handleSetBody('note', content);
                  }}
                />
                {validated &&
                  (body.note.trim() === '' || body.note === '<p><br></p>') && (
                    <div className="text-danger fs-10 mt-1">
                      Campo obrigatório.
                    </div>
                  )}
              </div>
            </div>
          </Form>
        </Card.Body>
        <Card.Footer className="d-flex justify-content-end">
          <Button
            variant="falcon-default"
            type="button"
            className="me-2"
            onClick={handleClose}
          >
            Fechar
          </Button>
          <Button
            variant="primary"
            type="button"
            onClick={onSubmit}
            style={{ borderRadius: '20px 0 0 20px' }}
            disabled={!currentContact?.email || loading}
            isLoading={loading}
          >
            {loading ? 'Salvando...' : 'Salvar'}
          </Button>

          <Dropdown>
            <Dropdown.Toggle
              as={CustomToggle}
              disabled={!currentContact?.email || loading}
            ></Dropdown.Toggle>

            <Dropdown.Menu className="flex-wrap" style={{ width: '150px' }}>
              <Dropdown.Item
                onClick={replyAndSetAsPending}
                disabled={ticketStatus?.status !== 'TO_DO'}
              >
                {loading ? 'Salvando...' : 'Salvar e Pausar'}
              </Dropdown.Item>
              <Dropdown.Item
                onClick={replyAndSetAsCompleted}
                disabled={
                  ticketStatus?.status === 'IN_PROGRESS' ||
                  ticketStatus?.status === 'COMPLETED'
                }
              >
                {loading ? 'Salvando...' : 'Salvar e Concluir'}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </Card.Footer>
      </Card>
    </>
  );
};

const CustomToggle = forwardRef(({ onClick, disabled }, ref) => (
  <Button
    style={{ borderRadius: '0 20px 20px 0', height: '100%' }}
    ref={ref}
    disabled={disabled}
    onClick={e => {
      e.preventDefault();
      onClick(e);
    }}
  >
    <FontAwesomeIcon icon={faChevronDown} />
  </Button>
));

export default ReplyNoteComponent;
